import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import {
  ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine,
} from "recharts";
import { Sparkles, Loader2, Compass, Save, Trash2, Plus, AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";

const GEO_SCOPES = [
  { v: "local", label: "Local (ville)" },
  { v: "cantonal", label: "Cantonal / régional" },
  { v: "national", label: "National (Suisse)" },
  { v: "international", label: "International" },
];

const AXIS_LABELS = {
  prix_positionnement: "Positionnement prix",
  largeur_offre: "Largeur d'offre",
  notoriete: "Notoriété",
};

export default function DispersionMarche() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [scope, setScope] = useState("cantonal");
  const [peers, setPeers] = useState([]);

  const load = async () => {
    try {
      const r = await api.get("/workspace/asymetries/dispersion/marche");
      setData(r.data);
      if (r.data?.analysis) {
        setPeers(r.data.analysis.peers || []);
        setScope(r.data.analysis.geo_scope || "cantonal");
      }
    } catch (e) {
      notify.error(fmtError(e, "Chargement impossible."));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const run = async () => {
    setRunning(true);
    try {
      const r = await api.post("/workspace/asymetries/dispersion/marche/run", { geo_scope: scope });
      setData((d) => ({ ...d, analysis: r.data }));
      setPeers(r.data.peers || []);
      notify.success(`${(r.data.peers || []).length} pairs identifiés.`);
    } catch (e) {
      notify.error(fmtError(e, "Génération impossible."));
    } finally { setRunning(false); }
  };

  const savePeers = async () => {
    setSaving(true);
    try {
      const r = await api.put("/workspace/asymetries/dispersion/marche/peers", { peers });
      setData((d) => ({ ...d, analysis: r.data }));
      notify.success("Pairs mis à jour — dispersion recalculée.");
    } catch (e) {
      notify.error(fmtError(e, "Sauvegarde impossible."));
    } finally { setSaving(false); }
  };

  const updatePeer = (i, k, v) => setPeers((arr) => arr.map((p, idx) => idx === i ? { ...p, [k]: v } : p));

  if (loading) return <div className="flex items-center justify-center py-16 text-zinc-500"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Chargement…</div>;

  const anchor = data?.anchor || {};
  const analysis = data?.analysis;
  const self = analysis?.self_position;
  const stats = analysis?.stats || {};

  return (
    <div className="space-y-4" data-testid="dispersion-marche">
      {/* Ancre Positionnement */}
      <div className={`noam-card p-4 ${anchor.has_profile ? "border-[#7F77DD]/25 bg-[#7F77DD]/[0.04]" : "border-[#EF9F27]/30 bg-[#EF9F27]/5"}`}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-xs">
            <Compass className="w-3.5 h-3.5 text-[#7F77DD]" />
            {anchor.has_profile ? (
              <span className="text-zinc-300" data-testid="dispersion-anchor">
                Ancré sur votre Positionnement : <strong className="text-white">{anchor.sector}{anchor.sector_free ? ` · ${anchor.sector_free}` : ""}</strong>
                {anchor.zone && <span className="text-zinc-400"> — {anchor.zone}</span>}
              </span>
            ) : (
              <span className="text-[#EF9F27] flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5" /> Complétez d'abord la phase 1 du <Link to="/app/positionnement" className="underline font-semibold">Positionnement</Link> — c'est l'ancre de cette analyse.
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <select value={scope} onChange={(e) => setScope(e.target.value)} className="noam-input text-xs h-8 w-44" data-testid="dispersion-scope-select">
              {GEO_SCOPES.map((g) => <option key={g.v} value={g.v}>{g.label}</option>)}
            </select>
            <button onClick={run} disabled={running || !anchor.has_profile} className="btn-primary text-xs disabled:opacity-50" data-testid="dispersion-run-btn">
              {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              {running ? "Analyse…" : analysis ? "Régénérer" : "Générer l'analyse IA"}
            </button>
          </div>
        </div>
      </div>

      {!analysis ? (
        <div className="noam-card p-10 text-center text-sm text-zinc-500" data-testid="dispersion-empty">
          Aucune analyse. L'IA va identifier 6-10 organisations qui font le même métier que vous
          dans le périmètre choisi, puis vous situer dans la distribution.
        </div>
      ) : (
        <>
          {/* Percentiles */}
          <div className="grid md:grid-cols-3 gap-3" data-testid="dispersion-percentiles">
            {Object.entries(AXIS_LABELS).map(([ax, label]) => {
              const s = stats[ax] || {};
              const p = s.percentile ?? 50;
              const color = p >= 80 || p <= 20 ? "#EF9F27" : "#71717A";
              return (
                <div key={ax} className="noam-card p-4">
                  <div className="noam-overline">{label}</div>
                  <div className="noam-display text-3xl font-bold mt-1" style={{ color }}>{p}<span className="text-base">e</span></div>
                  <div className="text-[10px] text-zinc-500 mt-1">
                    Vous : {s.self}/10 · médiane pairs : {s.median}/10 · étendue {s.min}–{s.max}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Scatter prix × largeur */}
          <div className="noam-card p-4">
            <div className="noam-overline mb-2">Carte de dispersion — Prix × Largeur d'offre</div>
            <ResponsiveContainer width="100%" height={300}>
              <ScatterChart margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
                <CartesianGrid stroke="#ffffff10" />
                <XAxis type="number" dataKey="x" domain={[0, 10]} name="Prix" tick={{ fill: "#71717A", fontSize: 10 }} label={{ value: "Positionnement prix →", fill: "#71717A", fontSize: 10, position: "insideBottom", offset: -5 }} />
                <YAxis type="number" dataKey="y" domain={[0, 10]} name="Largeur" tick={{ fill: "#71717A", fontSize: 10 }} label={{ value: "Largeur d'offre →", fill: "#71717A", fontSize: 10, angle: -90, position: "insideLeft" }} />
                <Tooltip content={({ payload }) => payload?.length ? (
                  <div className="bg-black/90 border border-white/20 rounded px-3 py-2 text-xs">
                    <div className="font-bold text-white">{payload[0].payload.name}</div>
                    <div className="text-zinc-400">Prix {payload[0].payload.x}/10 · Offre {payload[0].payload.y}/10</div>
                  </div>
                ) : null} />
                {self && <ReferenceLine x={self.prix_positionnement} stroke="#EF9F2755" />}
                {self && <ReferenceLine y={self.largeur_offre} stroke="#EF9F2755" />}
                <Scatter data={peers.map((p) => ({ x: p.prix_positionnement, y: p.largeur_offre, name: p.nom }))} fill="#378ADD" fillOpacity={0.75} />
                {self && <Scatter data={[{ x: self.prix_positionnement, y: self.largeur_offre, name: "VOUS" }]} fill="#EF9F27" shape="star" />}
              </ScatterChart>
            </ResponsiveContainer>
          </div>

          {/* Lecture IA */}
          {analysis.lecture && (
            <div className="noam-card p-5 border-[#EF9F27]/20" data-testid="dispersion-lecture">
              <div className="noam-overline text-[#EF9F27] mb-2">Lecture stratégique</div>
              <p className="text-sm text-zinc-300 leading-relaxed whitespace-pre-line">{analysis.lecture}</p>
              {self?.justification && <p className="text-xs text-zinc-500 mt-3 italic">Votre position : {self.justification}</p>}
            </div>
          )}

          {/* Table des pairs éditable */}
          <div className="noam-card p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="noam-overline">Pairs comparables ({peers.length}) — éditable</div>
              <div className="flex gap-2">
                <button onClick={() => setPeers((a) => [...a, { nom: "", localisation: "", taille: "", prix_positionnement: 5, largeur_offre: 5, notoriete: 5, commentaire: "" }])} className="btn-ghost text-xs" data-testid="dispersion-peer-add">
                  <Plus className="w-3 h-3" /> Ajouter
                </button>
                <button onClick={savePeers} disabled={saving} className="btn-primary text-xs disabled:opacity-50" data-testid="dispersion-peers-save">
                  {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Enregistrer
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-zinc-500 text-left border-b border-white/10">
                    <th className="py-2 pr-2">Pair</th><th className="pr-2">Localisation</th>
                    <th className="pr-2 w-20">Prix /10</th><th className="pr-2 w-20">Offre /10</th><th className="pr-2 w-20">Notoriété /10</th><th className="w-8" />
                  </tr>
                </thead>
                <tbody>
                  {peers.map((p, i) => (
                    <tr key={i} className="border-b border-white/5" data-testid={`dispersion-peer-row-${i}`}>
                      <td className="py-1.5 pr-2"><input value={p.nom} onChange={(e) => updatePeer(i, "nom", e.target.value)} className="noam-input text-xs h-7 w-full min-w-[140px]" /></td>
                      <td className="pr-2"><input value={p.localisation} onChange={(e) => updatePeer(i, "localisation", e.target.value)} className="noam-input text-xs h-7 w-full min-w-[100px]" /></td>
                      {["prix_positionnement", "largeur_offre", "notoriete"].map((k) => (
                        <td key={k} className="pr-2"><input type="number" min={0} max={10} step={0.5} value={p[k]} onChange={(e) => updatePeer(i, k, Number(e.target.value))} className="noam-input text-xs h-7 w-16" /></td>
                      ))}
                      <td><button onClick={() => setPeers((a) => a.filter((_, j) => j !== i))} className="text-zinc-600 hover:text-red-400 p-1"><Trash2 className="w-3 h-3" /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
